/**
 * API submission logic
 */

import { formatBirthday } from '../utils/dateHelpers.js';
import { t } from '../../i18n.js';
import { getTurnstileToken, resetTurnstile } from './turnstile.js';
import { trackError } from '../analytics/signupAnalytics.js';
import { getStepForApiField } from '../steps.js';

export async function submitSignup(state) {
    const birthday = formatBirthday(state.day, state.month, state.year);

    // Read before the reCAPTCHA await, so the POST carries the token the
    // wizard checked. An absent token is sent as no field. The backend logs
    // it as STATE_MISSING and accepts the submit.
    const turnstileToken = getTurnstileToken();

    // Get fresh reCAPTCHA token (v3 tokens expire after 2 minutes)
    let recaptchaToken = await getRecaptchaToken();

    const signupData = {
        name: state.name,
        email: state.email,
        birthday: birthday,
        sex: state.sex,
        username: state.username,
        // Trimmed so the submitted value is the one the step validated, and
        // the one the server stores after its own trim.
        password: (state.password || '').trim(),
        city_id: state.cityId,
        home_city_id: state.homeCityId,
        lang: window.__i18nConfig?.locale || 'en_US'
    };

    // Add reCAPTCHA token if available
    if (recaptchaToken) {
        signupData.googleResponse = recaptchaToken;
    }

    if (turnstileToken) {
        signupData['cf-turnstile-response'] = turnstileToken;
    }

    try {
        const result = await window.ApiClient.createAccount(signupData);

        // Store token if returned
        if (result.token) {
            storeAuthToken(result.token);
        }

        // Fire the buffered device-geo observation now that the JWT is stored.
        // Fire-and-forget (keepalive) so it never delays the redirect.
        reportGeoObservation(state);

        return { success: true, result };

    } catch (error) {
        console.error('[Signup] API error:', error);

        // A token is single-use, so the next attempt needs a fresh one. A
        // token minted while the POST ran is unused and stays. A POST without
        // a token consumed nothing, so the widget keeps its state.
        if (turnstileToken && getTurnstileToken() === turnstileToken) {
            resetTurnstile();
        }

        if (error.validationErrors) {
            reportServerValidationErrors(error.validationErrors, state.currentStep);

            return {
                success: false,
                validationErrors: error.validationErrors,
                // The fields can change while the POST is out. A refusal answers these values.
                sent: { email: signupData.email, username: signupData.username },
            };
        }

        // A 429 is a user outcome, a 5xx is not.
        trackError(state.currentStep, '', error.status ? `request_failed_${error.status}` : 'request_failed');

        return {
            success: false,
            error: error.message || t('auth.error_something_wrong')
        };
    }
}

// One event per rejected field, so each blocking field is countable on its own.
// A parameter with no step (captcha, spam) reports the step the submit left.
function reportServerValidationErrors(validationErrors, submitStep) {
    Object.keys(validationErrors).forEach((field) => {
        trackError(getStepForApiField(field) || submitStep, field, validationErrors[field]?.code);
    });
}

async function getRecaptchaToken() {
    if (typeof grecaptcha === 'undefined' || !grecaptcha.execute) {
        return document.getElementById('googleResponse')?.value;
    }

    try {
        const siteKey = document.querySelector('[data-recaptcha-sitekey]')?.getAttribute('data-recaptcha-sitekey');

        if (siteKey) {
            const token = await grecaptcha.execute(siteKey, { action: 'signup' });
            return token;
        } else {
            console.warn('[Signup] No reCAPTCHA site key found');
            return document.getElementById('googleResponse')?.value;
        }
    } catch (e) {
        console.warn('[Signup] reCAPTCHA execution failed:', e);
        return document.getElementById('googleResponse')?.value;
    }
}

// POST the device-geo observation captured during the city step. Skipped when
// nothing was captured. Never throws and never blocks the signup redirect.
function reportGeoObservation(state) {
    try {
        if (!state.geoObservation) {
            return;
        }
        window.ApiClient?.postGeoObservation(state.geoObservation, { signup_context: true })
            ?.catch(() => {});
    } catch (e) {
        // Recording an observation must never affect signup.
    }
}

function storeAuthToken(token) {
    try {
        const existingState = localStorage.getItem('redux-state') || '{}';
        const state = JSON.parse(existingState);
        state.auth = state.auth || {};
        state.auth.token = token;
        localStorage.setItem('redux-state', JSON.stringify(state));
    } catch (e) {
        console.warn('[Signup] Could not store auth token:', e);
    }
}
